/**
 * Tell a browser autofill or a paste into an address box from a buyer typing.
 *
 * The address inputs in guest checkout (guest-address-autocomplete.tsx) and
 * the saved-address form (shipping-input.tsx) look up the text when the
 * browser fills it in, because Google's `place_changed` never fires for
 * autofill. They must not look it up while the buyer is typing: a half-typed
 * street fails the lookup, and in guest checkout that opens the manual fields
 * on the partial text.
 *
 * Counting the characters one input event adds is not enough on its own.
 * Phone keyboards put a whole word in with one event: tapping "Amphitheatre"
 * in the iOS QuickType bar, committing a word on Gboard, swipe typing. So an
 * input only counts as autofill or paste when all of these hold:
 *
 *   1. It adds at least MIN_AUTOFILL_JUMP characters.
 *   2. It is not part of an IME composition and its `inputType` is not a
 *      typing type (`insertText`, `insertCompositionText`, ...). Chrome's
 *      autofill fires a plain `input` Event with no inputType; paste, drop and
 *      Firefox/Safari autofill use `insertFromPaste`, `insertFromDrop` and
 *      `insertReplacementText`.
 *   3. What it put in is more than one word. An address has a number and a
 *      street at the least; a keyboard suggestion is one word, sometimes with
 *      a trailing space. This also covers `insertReplacementText` when WebKit
 *      uses it for a word suggestion.
 */

/** Characters one input event must add to count as autofill or paste, not typing. */
export const MIN_AUTOFILL_JUMP = 3;

/** Input types a keyboard produces while typing, however long the text it adds. */
const TYPING_INPUT_TYPES = new Set(['insertText', 'insertCompositionText', 'insertLineBreak', 'insertParagraph']);

/** The text an edit put in: what `next` holds between the prefix and suffix it shares with `prev`. */
export function insertedText(prev: string, next: string): string {
  let start = 0;
  const max = Math.min(prev.length, next.length);
  while (start < max && prev[start] === next[start]) start += 1;
  let end = 0;
  while (end < max - start && prev[prev.length - 1 - end] === next[next.length - 1 - end]) end += 1;
  return next.slice(start, next.length - end);
}

/**
 * True when an `input` event on an address box is the browser filling it in
 * or a paste, and false when it is the buyer typing (see the file comment).
 */
export function isAutofillInput(event: Event, prev: string, next: string): boolean {
  if (next.length - prev.length < MIN_AUTOFILL_JUMP) return false;

  const { inputType, isComposing } = event as Partial<InputEvent>;
  if (isComposing) return false;
  if (inputType && TYPING_INPUT_TYPES.has(inputType)) return false;

  return /\S[\s,]+\S/.test(insertedText(prev, next).trim());
}
